/* REACT PACKAGE IMPORTS */
import { useRef, useState, useEffect, useMemo } from "react";
import { Form, Button, InputGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";

/* MUI PACKAGE IMPORTS */
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

/* INTERNAL IMPORTS */
import {
  userLoaded,
  userLogged,
  getAuthStatus,
  authError,
  hideError,
} from "../Features/Auth/Slices/authSlice";
import {
  LoginAuthAction,
  LoginWithAuthToken,
} from "../Features/Auth/Actions/AuthActions";
import { openModal } from "../Features/Errors/Slices/errorSlice";
import Loading from "../Components/Loading";
import "../styles/Login.css";

export default function CandidateLogin({ logo, authToken }) {
  const dispatch = useDispatch();

  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const navigate = useNavigate();

  const { status } = useSelector((state) => state.auth);
  const isError = useSelector(getAuthStatus) === "failed" ? true : false;
  const message = useSelector(authError);
  const isLoaded = useSelector(userLoaded);
  const user = useSelector(userLogged);

  const [validated, setValidated] = useState(false);
  const [isShowed, setIsShowed] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isValidUsername, setIsValidUsername] = useState(null);
  const [isValidPassword, setIsValidPassword] = useState(null);

  const mounted = useRef(null);
  const userRef = useRef();
  const passRef = useRef();

  /* UseEffect functions */
  useEffect(() => {
    document.title = "VX";
    if (authToken) return;
    userRef.current.focus();
  }, [authToken]);

  useEffect(() => {
    if (message) {
      dispatch(
        openModal({
          message: message,
          severity: "warning",
        })
      );
    }
  }, [isError]);

  useEffect(() => dispatch(hideError()), [username, password, dispatch]);

  useEffect(() => {
    mounted.current = true;
    const queryUser = query.get("username");
    const queryPassword = query.get("password");

    if (!queryUser || !queryPassword) return;
    if (mounted.current) {
      setUsername(queryUser);
      setPassword(queryPassword);
      const credentials = {
        username: queryUser,
        password: queryPassword,
      };
      dispatch(LoginAuthAction(credentials))
        .unwrap()
        .then((response) => {
        })
        .catch(() => navigate("/candidate/error", { replace: true }));
    }
    return () => (mounted.current = false);
  }, [query]);

  useEffect(() => {
    mounted.current = true;

    if (!isLoaded) return;
    if (mounted.current && status === "succeeded") {
      navigate("/candidate", { replace: true });
    }
    return () => (mounted.current = false);
  }, [isLoaded, user, navigate]);

  useEffect(() => {
    mounted.current = true;

    if (!authToken) return;
    if (mounted.current) {
      const credentials = { AUTH_TOKEN: authToken };
      dispatch(LoginWithAuthToken(credentials));
    }
    return () => (mounted.current = false);
  }, [authToken]);

  const validateUsernameAndPassword = () => {
    // Case: Password and Username fields Blank
    if (username === "" && password === "") {
      dispatch(
        openModal({
          message: `Fields cannot be left empty`,
          severity: "warning",
        })
      );
      setIsValidUsername("false");
      setIsValidPassword("false");
      userRef.current.focus();
      return false;
    }
    // Case: Username field blank
    else if (username === "") {
      dispatch(
        openModal({
          message: `Username cannot be left empty`,
          severity: "warning",
        })
      );
      setIsValidUsername("false");
      setValidated(false);
      userRef.current.focus();
      return false;
    }
    // Case: Password field blank
    else if (password === "") {
      dispatch(
        openModal({
          message: `Password cannot be left empty`,
          severity: "warning",
        })
      );
      setIsValidPassword("false");
      setValidated(false);
      passRef.current.focus();
      return false;
    }
    // Case: Happy path, no validation errors
    else {
      setIsValidUsername("true");
      setIsValidPassword("true");
      return true;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const credentials = {
      username: username,
      password: password,
    };
    if (validateUsernameAndPassword()) dispatch(LoginAuthAction(credentials));
    else event.stopPropagation();
  };

  const handleUsernameChange = (event) => {
    if (isValidUsername) setIsValidUsername(null);
    setUsername(event.target.value);
  };

  const handlePasswordChange = (event) => {
    if (isValidPassword) setIsValidPassword(null);
    setPassword(event.target.value);
  };

  return (
    <main className="Login position-relative">
      {authToken ? (
        <Loading />
      ) : (
        <div className="Login-container box position-absolute top-50 start-50 translate-middle p-4">
          <div className="d-flex flex-column mx-5 my-2 justify-content-between align-items-center">
            {/* Invisible h1 added for accessibility requirements */}
            <h1 className="visually-hidden">VICTVS v one</h1>
            <img src={logo} alt="Logo" className="h-32" />
          </div>
          <Form
            id="form"
            className="mx-5 my-4 p-2"
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
          >
            <Form.Group className="mb-4">
              <Form.Label id="login-username">Username</Form.Label>
              <Form.Control
                aria-labelledby="login-username"
                title="username"
                type="text"
                ref={userRef}
                value={username}
                onChange={(event) => handleUsernameChange(event)}
                isInvalid={isValidUsername === "false"}
                isValid={isValidUsername === "true"}
                data-cy="usernameField"
                required
              />
              <Form.Text>
                <Link
                  className="d-block text-end text-decoration-none mt-1 link-font-size"
                  to="/forgot-username"
                >
                  Forgot Username?
                </Link>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label id="login-password">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  data-cy="passwordField"
                  aria-labelledby="login-password"
                  title="password"
                  type={!isShowed ? "password" : "text"}
                  value={password}
                  onChange={(event) => handlePasswordChange(event)}
                  ref={passRef}
                  isInvalid={isValidPassword === "false"}
                  isValid={isValidPassword === "true"}
                  required
                />
                <InputGroup.Text className="rounded-end">
                  <i
                    onClick={() => setIsShowed(!isShowed)}
                    className="hover-pointer"
                  >
                    {!isShowed ? (
                      <VisibilityIcon fontSize="small" />
                    ) : (
                      <VisibilityOffIcon fontSize="small" />
                    )}
                  </i>
                </InputGroup.Text>
              </InputGroup>
              <Form.Text>
                <Link
                  className="d-block text-end text-decoration-none mt-1 link-font-size"
                  to="/forgot-password"
                >
                  Forgot Password?
                </Link>
              </Form.Text>
            </Form.Group>

            <div className="mb-4">
              <Button
                variant="dark"
                type="submit"
                className="mt-1 container"
                data-cy="signInButton"
              >
                Sign in
              </Button>
            </div>
          </Form>
        </div>
      )}
    </main>
  );
}
